import {
	Checkbox,
	H3,
	Table,
	TableBody,
	TableCell,
	TableHeader,
	TableRow,
} from "@dune/ui";
import {
	designerTypeToLabel,
	getParticipantCompanyName,
	traductActivities,
} from "@dune/utils";
import { formatCurrency } from "@dune/utils";
import { useShouldDisplayHTAmount } from "../../hooks/useParticipantToSubmissionForm";
import { ParticipantDocuments } from "./ParticipantDocuments";
import {
	BaseSubmissionParticipantTableProps,
	ParticipantsTableCell,
	ParticipantsTableHead,
} from "./participantsTable.helper";

interface DesignersTableProps extends BaseSubmissionParticipantTableProps {}

export const DesignersTable = ({
	submissionParticipants,
	constructionSiteId,
	selectedParticipantId,
	onParticipantSelect,
	readonly,
}: DesignersTableProps) => {
	const { shouldDisplayHT } = useShouldDisplayHTAmount(constructionSiteId);

	const totalFees = submissionParticipants.reduce(
		(acc, submissionParticipant) => {
			const participantFees =
				shouldDisplayHT && submissionParticipant.totalFeesHT != null
					? submissionParticipant.totalFeesHT
					: submissionParticipant.totalFeesTTC;
			return acc + participantFees;
		},
		0,
	);

	return (
		<div className="mb-4">
			<H3>CONCEPTEURS</H3>
			<div className="bg-white rounded-lg border overflow-x-hidden">
				<Table className="table-fixed w-full">
					<TableHeader>
						<TableRow>
							<ParticipantsTableHead className="w-64">
								Type
							</ParticipantsTableHead>
							<ParticipantsTableHead className="w-48">
								Raison sociale
							</ParticipantsTableHead>
							<ParticipantsTableHead className="w-32">
								Missions
							</ParticipantsTableHead>
							<ParticipantsTableHead className="w-48">
								Honoraires {shouldDisplayHT ? "(HT)" : "(TTC)"}
							</ParticipantsTableHead>
							<ParticipantsTableHead>
								Pièces justificatives
							</ParticipantsTableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{submissionParticipants.length > 0 ? (
							submissionParticipants.map((submissionParticipant) => {
								const missions = traductActivities(
									submissionParticipant.totalMissionsOrActivitiesOrMaterialTypes,
								);
								const fees =
									shouldDisplayHT && submissionParticipant.totalFeesHT != null
										? submissionParticipant.totalFeesHT
										: submissionParticipant.totalFeesTTC;
								const companyName = getParticipantCompanyName(
									submissionParticipant.participant,
								);
								const designerType = submissionParticipant.designerType
									? designerTypeToLabel[submissionParticipant.designerType]
									: "-";

								const isSelected =
									selectedParticipantId ===
									submissionParticipant.participant.id;

								return (
									<TableRow key={submissionParticipant.id}>
										<ParticipantsTableCell>
											<div className="flex items-center gap-4">
												<Checkbox
													id={`designer-${submissionParticipant.id}`}
													checked={isSelected}
													onCheckedChange={(checked) => {
														onParticipantSelect(
															submissionParticipant.participant.id,
															checked === true,
														);
													}}
												/>
												<label
													htmlFor={`designer-${submissionParticipant.id}`}
													className="capitalize truncate"
												>
													{designerType}
												</label>
											</div>
										</ParticipantsTableCell>
										<ParticipantsTableCell tooltipContent={companyName}>
											{companyName}
										</ParticipantsTableCell>
										<ParticipantsTableCell
											tooltipContent={
												<div className="flex flex-col gap-1">
													{missions.map((activity) => (
														<span key={activity}>{activity}</span>
													))}
												</div>
											}
										>
											{typeof missions === "string"
												? missions
												: missions.join(", ")}
										</ParticipantsTableCell>
										<ParticipantsTableCell>
											{formatCurrency(fees)}
										</ParticipantsTableCell>
										<ParticipantsTableCell>
											<ParticipantDocuments
												submissionParticipant={submissionParticipant}
												constructionSiteId={constructionSiteId}
												readonly={readonly}
											/>
										</ParticipantsTableCell>
									</TableRow>
								);
							})
						) : (
							<TableRow>
								<TableCell
									colSpan={5}
									className="text-center text-gray-500 py-4"
								>
									Aucun concepteur
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
				<div className="flex justify-end px-6 py-4 font-bold text-gray-700 border-t">
					Total concepteurs
					<span className="ml-4 w-40 text-right">
						{formatCurrency(totalFees)}
					</span>
				</div>
			</div>
		</div>
	);
};
